import type { TicketCategory, TicketPriority, TicketService, TicketStatus } from "@/lib/types";

/**
 * How a ticket's state is shown, in one place.
 *
 * Both surfaces render the same five statuses, and a word for each that is
 * written for the reader rather than taken from the column. `waiting_on_us`
 * is the database's name for it; a customer should be told "with support".
 */
export const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "Open",
  waiting_on_us: "With support",
  waiting_on_customer: "Waiting for you",
  resolved: "Resolved",
  closed: "Closed",
};

/**
 * The same five from the staff side, where "waiting for you" is the other
 * person. One shared set of labels would be wrong on one of the two screens,
 * and the screen it is wrong on is whichever one somebody reads next.
 */
export const STAFF_STATUS_LABEL: Record<TicketStatus, string> = {
  open: "New",
  waiting_on_us: "Needs a reply",
  waiting_on_customer: "Waiting on customer",
  resolved: "Resolved",
  closed: "Closed",
};

/**
 * Which status colour each state borrows.
 *
 * Deliberately the service-state tokens rather than a second palette: a
 * reader has already learned on every other screen that amber means somebody
 * has to do something and green means it is finished.
 */
export const STATUS_TONE: Record<TicketStatus, string> = {
  open: "degraded",
  waiting_on_us: "degraded",
  waiting_on_customer: "building",
  resolved: "running",
  closed: "stopped",
};

export const PRIORITY_LABEL: Record<TicketPriority, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
};

export const CATEGORY_LABEL: Record<TicketCategory, string> = {
  account: "Account",
  billing: "Billing",
  technical: "Technical",
  abuse: "Abuse",
  other: "Something else",
};

/**
 * What each category is for, shown under the choice.
 *
 * Five categories with no explanation is five words from which somebody picks
 * the first plausible one. The sentence is what makes the choice mean
 * anything to whoever picks the ticket up.
 */
export const CATEGORY_HINT: Record<TicketCategory, string> = {
  account:
    "Changing the name on the account, closing it, or anything else you cannot change yourself.",
  billing: "An invoice, a payment, a VAT identifier, or what something cost.",
  technical: "A service that will not start, a build that fails, something that is slow.",
  abuse: "Something hosted here that should not be.",
  other: "Anything that is none of the above.",
};

export const CATEGORIES: TicketCategory[] = ["account", "billing", "technical", "abuse", "other"];

export const PRIORITIES: TicketPriority[] = ["low", "normal", "high", "urgent"];

/** Whether the ticket still needs anybody. */
export function isOpen(status: TicketStatus): boolean {
  return status === "open" || status === "waiting_on_us" || status === "waiting_on_customer";
}

/**
 * The services on a ticket, short enough for a table row.
 *
 * # WHY A STRING AND NOT THE CHIPS
 *
 * The staff queue is a compact table, and a compact row is one line: that is
 * the rule the whole table idiom rests on, because a row whose height depends
 * on its contents makes the grid ragged. Chips would wrap. So the queue gets
 * one service named and a count of the rest, in the cell's secondary slot,
 * and the full list is on the ticket.
 *
 * The project is included on the one that is named, because "api" on its own
 * is the ambiguity this whole field exists to remove.
 */
export function serviceSummary(services: TicketService[] | undefined): string | undefined {
  if (!services || services.length === 0) return undefined;
  const first = services[0]!;
  const name = `${first.project}/${first.name}`;
  return services.length === 1 ? name : `${name} +${services.length - 1}`;
}
